/**
 * Rolling priced shifts up into a margin per site and per client contract.
 *
 * Labour cost is gross pay plus the employer's on-costs. A line with any
 * unpriced site states no margin at all: revenue for half a contract against
 * the cost of all of it would read as a loss that is not there.
 */

import { roundMoney } from '../payroll/time';
import type {
  ContractMargin,
  CostedShift,
  MarginLine,
  MarginReport,
  MarginStatus,
  PayBreakdown,
  ProfitabilityContract,
  ProfitabilitySettings,
  SiteMargin,
} from './types';

export const NO_CLIENT_NAME = 'Sites without a client';

const emptyBreakdown = (): PayBreakdown => ({
  basePay: 0,
  overtimePremium: 0,
  sundayPremium: 0,
  publicHolidayPremium: 0,
  unsocialPremium: 0,
  minimumShiftTopUp: 0,
});

/** A margin that only misses its target in the last binary digit has met it. */
const FLOAT_TOLERANCE = 1e-9;

export function marginStatus(
  margin: number | null,
  marginPercent: number | null,
  targetMarginPercent: number,
): MarginStatus {
  if (margin === null) return 'no_charge_rate';
  if (margin < 0) return 'loss';
  if (marginPercent === null || marginPercent < targetMarginPercent - FLOAT_TOLERANCE) return 'below_target';
  return 'on_target';
}

/** One line from a set of priced shifts. */
export function buildMarginLine(costed: CostedShift[], settings: ProfitabilitySettings): MarginLine {
  const breakdown = emptyBreakdown();
  const guardIds = new Set<string>();
  let paidMinutes = 0;
  let billableMinutes = 0;
  let weightedBillableHours = 0;
  let grossPay = 0;
  let revenue: number | null = costed.length > 0 ? 0 : null;
  let draftShiftCount = 0;
  let fallbackRateShiftCount = 0;

  costed.forEach((entry) => {
    guardIds.add(entry.shift.guardId);
    paidMinutes += entry.paidMinutes;
    billableMinutes += entry.billableMinutes;
    weightedBillableHours += entry.weightedBillableHours;
    grossPay += entry.pay.estimatedTotalPay;
    if (!entry.shift.isPublished) draftShiftCount += 1;
    if (entry.usedFallbackRate) fallbackRateShiftCount += 1;

    (Object.keys(breakdown) as (keyof PayBreakdown)[]).forEach((key) => {
      breakdown[key] += entry.breakdown[key];
    });

    revenue = revenue === null || entry.revenue === null ? null : revenue + entry.revenue;
  });

  (Object.keys(breakdown) as (keyof PayBreakdown)[]).forEach((key) => {
    breakdown[key] = roundMoney(breakdown[key]);
  });

  grossPay = roundMoney(grossPay);
  const onCost = roundMoney(grossPay * (settings.employerOnCostPercent / 100));
  const labourCost = roundMoney(grossPay + onCost);
  const billableHours = billableMinutes / 60;
  const roundedRevenue = revenue === null ? null : roundMoney(revenue);
  const margin = roundedRevenue === null ? null : roundMoney(roundedRevenue - labourCost);
  const marginPercent = margin !== null && roundedRevenue !== null && roundedRevenue > 0
    ? (margin / roundedRevenue) * 100
    : null;

  return {
    shiftCount: costed.length,
    draftShiftCount,
    guardCount: guardIds.size,
    paidHours: paidMinutes / 60,
    billableHours,
    weightedBillableHours,
    grossPay,
    onCost,
    labourCost,
    breakdown,
    fallbackRateShiftCount,
    revenue: roundedRevenue,
    margin,
    marginPercent,
    costPerBillableHour: billableHours > 0 ? labourCost / billableHours : null,
    averageChargeRate: roundedRevenue !== null && weightedBillableHours > 0
      ? roundedRevenue / weightedBillableHours
      : null,
    status: marginStatus(margin, marginPercent, settings.targetMarginPercent),
  };
}

const sortKey = (line: MarginLine): number => {
  if (line.margin === null) return Number.POSITIVE_INFINITY;
  // Cost against no revenue at all has no percentage; it is still the worst there is.
  if (line.marginPercent === null) return line.margin < 0 ? Number.NEGATIVE_INFINITY : Number.MAX_VALUE;
  return line.marginPercent;
};

/** Worst first: a loss, then the thinnest margin; lines with no charge rate last. */
function sortWorstFirst<T extends MarginLine>(lines: T[], nameOf: (line: T) => string): T[] {
  return lines.sort((left, right) => {
    const leftKey = sortKey(left);
    const rightKey = sortKey(right);
    if (leftKey !== rightKey) return leftKey < rightKey ? -1 : 1;
    return nameOf(left).localeCompare(nameOf(right));
  });
}

function groupBy<T>(items: T[], keyOf: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const key = keyOf(item);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  });
  return groups;
}

const NO_CLIENT_KEY = '';

/**
 * Margin by contract, each with its sites. Only what was rostered appears: a
 * contract or site with no shifts in the period has no cost and no revenue to
 * state.
 */
export function buildMarginReport(
  costed: CostedShift[],
  contracts: ProfitabilityContract[],
  settings: ProfitabilitySettings,
): MarginReport {
  const contractNames = new Map(contracts.map((contract) => [contract.id, contract.name]));

  const contractMargins: ContractMargin[] = [];
  groupBy(costed, (entry) => entry.site.clientContractId ?? NO_CLIENT_KEY).forEach((contractShifts, key) => {
    const sites: SiteMargin[] = [];
    groupBy(contractShifts, (entry) => entry.site.id).forEach((siteShifts) => {
      const site = siteShifts[0].site;
      sites.push({
        ...buildMarginLine(siteShifts, settings),
        siteId: site.id,
        siteName: site.name,
        // The latest shift carries the rate a reader would call current.
        chargeRate: siteShifts.reduce((latest, entry) => (
          entry.shift.dateKey >= latest.shift.dateKey ? entry : latest
        )).chargeRate,
      });
    });
    sortWorstFirst(sites, (site) => site.siteName);

    contractMargins.push({
      ...buildMarginLine(contractShifts, settings),
      contractId: key === NO_CLIENT_KEY ? null : key,
      contractName: key === NO_CLIENT_KEY ? NO_CLIENT_NAME : contractNames.get(key) ?? 'Unknown client',
      sites,
      unpricedSiteCount: sites.filter((site) => site.status === 'no_charge_rate').length,
    });
  });

  sortWorstFirst(contractMargins, (contract) => contract.contractName);

  const pricedContractIds = new Set(
    contractMargins.filter((contract) => contract.status !== 'no_charge_rate').map((contract) => contract.contractId),
  );
  const pricedShifts = costed.filter((entry) => pricedContractIds.has(entry.site.clientContractId));
  const unpriced = contractMargins.filter((contract) => contract.status === 'no_charge_rate');

  return {
    contracts: contractMargins,
    totals: buildMarginLine(costed, settings),
    priced: buildMarginLine(pricedShifts, settings),
    unpricedContractCount: unpriced.length,
    unpricedLabourCost: roundMoney(unpriced.reduce((total, contract) => total + contract.labourCost, 0)),
    lossMakingCount: contractMargins.filter((contract) => contract.status === 'loss').length,
    belowTargetCount: contractMargins.filter((contract) => contract.status === 'below_target').length,
  };
}
